import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";

import { useDocuments, useWorkspaces } from "~/state";
import { useAppActor, useAppActorSelector } from "~/state/machines/app/app.machine";
import { LOCAL_WORKSPACE_NAME } from "~/state/types/workspace.types";

export const useDocsList = ({ facet }: { facet: string | null }) => {
	const router = useRouter();

	const { data: workspaces } = useWorkspaces();
	const { data: documents, refetch: refetchDocuments } = useDocuments();

	const appActor = useAppActor();

	useEffect(() => {
		const subscription = appActor.on("document.created", ({ payload }) => {
			void router.push(`/d/${LOCAL_WORKSPACE_NAME}/${payload.id}`);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [appActor, router]);

	const createDocument = useCallback(() => {
		appActor.send({
			type: "documentList.createDocument",
			payload: {
				/* use defaults */
			},
		});
	}, [appActor]);
	const isCreatingDocument = useAppActorSelector((state) =>
		state.matches({ documentList: { local: "creating" } })
	);

	const filteredDocuments = useMemo(() => {
		let result;
		if (facet === null) {
			result = Object.values(documents);
		} else if (facet === "local") {
			result = Object.values(documents).filter(
				(document) => Boolean(document.path) && document.workspaces.length === 0
			);
		} else {
			result = Object.values(documents).filter((document) =>
				document.workspaces.some((workspace) => workspace.name === facet)
			);
		}

		// sort by last modified date
		result = result.sort((a, b) => {
			const aDate = new Date(a.updatedAt);
			const bDate = new Date(b.updatedAt);
			return bDate.getTime() - aDate.getTime();
		});

		return result;
	}, [documents, facet]);

	return {
		workspaces,
		documents: {
			data: filteredDocuments,
			isError: false,
			isPending: false,
			isSuccess: true,
			refetch: () => {
				refetchDocuments();
			},
		},
		createDocument,
		isCreatingDocument,
	};
};
